import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CaretRightFilled from "@ant-design/icons/CaretRightFilled";
import WarningFilled from "@ant-design/icons/WarningFilled";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import iconRediscover from "assets/images/icons/icn_btn_rediscover.svg";
import iconSwitch from "assets/images/icons/icn_dashboard_switch_off.svg";
import ModalContainer from "components/ModalContainer";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { getDefaultSelectProp, getDefaultTableProp } from "../../theme";
import modalStyle from "../modal.module.scss";

const AdoptionModal = ({
  open,
  devices,
  sites,
  scopeLevel,
  selectedSiteId,
  setSelectedSiteId,
  submitted,
  showServiceChange,
  preConfirmData,
  preConfirmLoading,
  onAdopt,
  onConfirmServiceChange,
  onCancelServiceChange,
  onClose,
}) => {
  const sitesToRender = (preConfirmData || []).filter((item) => item.service?.length > 0);
  const isInsufficient = sitesToRender.some((item) => item.sufficientCredits === false);
  const serviceNameMapping = { healthAI: "Health AI Insight", nmsPro: "NMS Pro", unityPro: "Nuclias Unity Portal" };

  return (
    <ModalContainer display={open} modalWidth={showServiceChange ? "600px" : "90p"}>
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography color={showServiceChange ? "#172664" : "#707ca8"} fontWeight={600} fontSize={showServiceChange ? "18px" : "20px"}>
            {showServiceChange ? "Confirm Service Change" : "Device Adoption"}
          </Typography>
          <IconButton onClick={showServiceChange ? onCancelServiceChange : onClose} size="small" aria-label="Close" sx={{ mr: "-20px" }}>
            {showServiceChange ? <CloseOutlined style={{ color: "rgba(0,0,0,0.45)", fontSize: 18 }} /> : <CloseIcon />}
          </IconButton>
        </Box>

        {showServiceChange ? (
          <Stack spacing={2.5} sx={{ mt: 2 }}>
            <Box sx={{ overflowY: "auto", maxHeight: "75vh" }}>
              <Stack spacing={2}>
                {sitesToRender.map((siteItem, index) => {
                  const serviceText = siteItem.service.map((service) => serviceNameMapping[service] || service).join(", ") || "Subscribed Services";
                  const actionText = siteItem.action === "add" ? "Added Device" : siteItem.action === "remove" ? "Remove Device" : siteItem.action;
                  const dailyRate = siteItem.dailyRate || 0;
                  const totalCredits = dailyRate * (siteItem.remainingDays || 0);
                  return (
                    <Stack key={`${siteItem.siteId || siteItem.siteName}-${index}`} spacing={2}>
                      <Stack spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CaretRightFilled style={{ fontSize: 14, color: "#172664" }} />
                          <Typography sx={{ color: "rgba(0,0,0,0.85)", fontSize: 15, fontWeight: "bold" }}>{siteItem.siteName || "Site"}</Typography>
                        </Stack>
                        <Typography sx={{ color: "#172664", fontSize: 16, fontWeight: "bold" }}>Service: {serviceText}</Typography>
                        <Divider />
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1}><Typography variant="body2" sx={{ minWidth: 110, color: "rgba(0,0,0,0.45)" }}>Action:</Typography><Typography variant="body2" sx={{ color: "#172664", fontWeight: "bold" }}>{actionText}</Typography></Stack>
                          <Stack direction="row" spacing={1}><Typography variant="body2" sx={{ minWidth: 110, color: "rgba(0,0,0,0.45)" }}>New Devices:</Typography><Typography variant="body2" sx={{ color: "#008514", fontWeight: "bold" }}>{siteItem.deviceCount} (Total: {siteItem.deviceTotalCount})</Typography></Stack>
                          <Stack direction="row" spacing={1}><Typography variant="body2" sx={{ minWidth: 110, color: "rgba(0,0,0,0.45)" }}>Rate:</Typography><Typography variant="body2" sx={{ color: "#373737", fontWeight: "bold" }}>{siteItem.rate || ""}</Typography></Stack>
                        </Stack>
                      </Stack>
                      <Box sx={{ p: 2, border: "1px solid rgba(0,0,0,0.15)", borderRadius: "12px" }}>
                        <Typography sx={{ color: "#172664", fontSize: 14, fontWeight: "bold", mb: 2 }}>This Month’s Prorated Credit Deduction</Typography>
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between"><Typography color="textSecondary">Daily Rate</Typography><Typography sx={{ fontWeight: "bold", color: "#373737" }}><Box component="span" sx={{ color: "#008514" }}>{dailyRate.toLocaleString()}</Box> Credits / day</Typography></Stack>
                          <Stack direction="row" justifyContent="space-between"><Typography color="textSecondary">Remaining Days</Typography><Typography sx={{ fontWeight: "bold", color: "#373737" }}><Box component="span" sx={{ color: "#008514" }}>{siteItem.remainingDays}</Box> Days</Typography></Stack>
                          <Stack direction="row" justifyContent="space-between"><Typography color="textSecondary">Total</Typography><Typography sx={{ fontWeight: "bold", color: "#373737" }}><Box component="span" sx={{ color: "#008514" }}>{totalCredits.toLocaleString()}</Box> Credits</Typography></Stack>
                        </Stack>
                      </Box>
                    </Stack>
                  );
                })}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <WarningFilled style={{ color: "#D1003B", fontSize: 18 }} />
                  <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.85)", fontWeight: "bold" }}>
                    {isInsufficient ? "Insufficient credits. Please contact your Organization Super Admin." : "Changes take effect immediately upon confirmation."}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            <Stack direction="row" justifyContent="center" spacing={2}>
              <OutlineButton onClick={onCancelServiceChange} width="120px" height="36px" sx={{ borderRadius: "20px" }}>Cancel</OutlineButton>
              <SolidButton onClick={onConfirmServiceChange} disabled={isInsufficient} width="120px" height="36px" sx={{ borderRadius: "20px", bgcolor: "#172664" }}>Confirm</SolidButton>
            </Stack>
          </Stack>
        ) : !submitted ? (
          <>
            <Box className={modalStyle.modal_Block} sx={{ my: 2, maxHeight: 400, overflowY: "auto", display: "block", textAlign: "left" }}>
              <TableContainer sx={{ overflowY: "auto" }}>
                <Table>
                  <TableHead sx={getDefaultTableProp().tableHead.sx}>
                    <TableRow sx={getDefaultTableProp().tableHead.tableRow.sx}>
                      {["Model Name", "MAC address", "IP address"].map((label) => (
                        <TableCell key={label} sx={getDefaultTableProp().tableHead.tableCell.text}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((device, index) => (
                      <TableRow key={device.id} sx={{ ...getDefaultTableProp(index).tableBody.tableRow, cursor: "default", height: 88 }}>
                        <TableCell sx={getDefaultTableProp().tableBody.tableCell}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <img src={iconSwitch} alt="Switch" style={{ width: 60, marginRight: 8 }} />
                            {device.modelName || "-"}
                          </Box>
                        </TableCell>
                        <TableCell sx={getDefaultTableProp().tableBody.tableCell}>{device.macAddress || "-"}</TableCell>
                        <TableCell sx={getDefaultTableProp().tableBody.tableCell}>{device.ipAddress || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {scopeLevel === "org" && (
              <>
                <Box sx={{ px: 2, py: 1, textAlign: "center", fontSize: 18 }}>
                  Please ensure the devices' IP settings allow them to reach the controller before proceeding. Incorrect IP settings may prevent the devices from being adopted successfully.
                </Box>
                <Box sx={{ my: 3, display: "flex", justifyContent: "center", alignItems: "center", gap: 3 }}>
                  <Typography>Add to site</Typography>
                  <Select
                    style={{ ...getDefaultSelectProp().style, width: "300px" }}
                    value={selectedSiteId}
                    displayEmpty
                    sx={{ ...getDefaultSelectProp().sx, width: "300px" }}
                    MenuProps={{ ...getDefaultSelectProp().menuProps }}
                    onChange={(event) => setSelectedSiteId(event.target.value)}
                  >
                    <MenuItem value="" disabled>Select site</MenuItem>
                    {sites.map((site) => <MenuItem key={site.id} value={site.id}>{site.name}</MenuItem>)}
                    <MenuItem value="inventory">Inventory</MenuItem>
                  </Select>
                </Box>
              </>
            )}
            <Box className={modalStyle.modal_operation}>
              <OutlineButton
                className={modalStyle.modal_cancelBtn}
                style={{ width: 195, height: 42, fontSize: 16 }}
                onClick={onClose}
              >Cancel</OutlineButton>
              <SolidButton
                className={modalStyle.modal_saveBtn}
                style={{ width: 195, height: 42, fontSize: 16 }}
                disabled={!selectedSiteId || !devices.length || preConfirmLoading}
                onClick={onAdopt}
              >{preConfirmLoading ? <CircularProgress size={20} color="inherit" /> : "Next"}</SolidButton>
            </Box>
          </>
        ) : (
          <>
            <Box className={modalStyle.modal_Block} sx={{ my: 2, maxHeight: 400, overflowY: "auto", display: "block", textAlign: "left" }}>
              <TableContainer sx={{ overflowY: "auto" }}>
                <Table>
                  <TableHead sx={getDefaultTableProp().tableHead.sx}>
                    <TableRow sx={getDefaultTableProp().tableHead.tableRow.sx}>
                      {["Model Name", "MAC address", "IP address", "Result"].map((label) => (
                        <TableCell key={label} sx={getDefaultTableProp().tableHead.tableCell.text}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((device, index) => (
                      <TableRow key={device.id} sx={{ ...getDefaultTableProp(index).tableBody.tableRow, cursor: "default" }}>
                        <TableCell sx={getDefaultTableProp().tableBody.tableCell}>{device.modelName || "-"}</TableCell>
                        <TableCell sx={getDefaultTableProp().tableBody.tableCell}>{device.macAddress || "-"}</TableCell>
                        <TableCell sx={getDefaultTableProp().tableBody.tableCell}>{device.ipAddress || "-"}</TableCell>
                        <TableCell sx={getDefaultTableProp().tableBody.tableCell}>
                          {device.adoptResult ? (
                            <Typography component="span" sx={{ color: device.adoptResult.code === 0 ? "#00a52e" : "#ff0000", fontWeight: "bold" }}>
                              {device.adoptResult.message || (device.adoptResult.code === 0 ? "Success" : "Failed")}
                            </Typography>
                          ) : <img src={iconRediscover} alt="Loading" style={{ width: 20, height: 20, marginRight: 5 }} />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box className={modalStyle.modal_operation}>
              <SolidButton
                className={modalStyle.modal_saveBtn}
                disabled={!devices.length || devices.some((device) => !device.adoptResult)}
                onClick={onClose}
              >Close</SolidButton>
            </Box>
          </>
        )}
      </Box>
    </ModalContainer>
  );
};

export default AdoptionModal;
